"use client";

import { XCircleIcon } from "@heroicons/react/24/outline";
import { CheckCircleIcon } from "@heroicons/react/24/solid";
import ImageModal from "@/components/ui/ImageModal";
import type { PhotoCoachExample } from "@/lib/listing/photoCoachExamples";

export type PhotoCoachExampleSlide = {
  example: PhotoCoachExample;
  url: string;
};

type PhotoCoachExampleModalProps = {
  isOpen: boolean;
  onClose: () => void;
  slides: PhotoCoachExampleSlide[];
  currentIndex: number;
  onIndexChange: (index: number) => void;
};

function PhotoCoachExampleCaption({ example }: { example: PhotoCoachExample }) {
  const isGood = example.variant === "good";
  const badgeClass = isGood
    ? "bg-primary-100 text-primary-800"
    : "bg-rose-100 text-rose-800";
  const BadgeIcon = isGood ? CheckCircleIcon : XCircleIcon;

  return (
    <div className="space-y-1.5 text-left">
      <div className="flex flex-wrap items-center gap-2">
        <p className="text-sm font-semibold text-foreground">
          {example.slotTitle}
        </p>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${badgeClass}`}
        >
          <BadgeIcon className="h-3.5 w-3.5" aria-hidden />
          {isGood ? "Good" : "Avoid"}
        </span>
      </div>
      <p className="text-sm leading-relaxed text-text-light">
        {example.description}
      </p>
    </div>
  );
}

export default function PhotoCoachExampleModal({
  isOpen,
  onClose,
  slides,
  currentIndex,
  onIndexChange,
}: PhotoCoachExampleModalProps) {
  if (!slides.length) {
    return null;
  }

  const safeIndex = Math.min(Math.max(0, currentIndex), slides.length - 1);
  const currentSlide = slides[safeIndex];

  return (
    <ImageModal
      isOpen={isOpen}
      onClose={onClose}
      images={slides.map((slide) => ({
        id: slide.example.id,
        url: slide.url,
      }))}
      title={currentSlide.example.slotTitle}
      currentImageIndex={safeIndex}
      onImageChange={onIndexChange}
      renderCaption={(index) => (
        <PhotoCoachExampleCaption example={slides[index].example} />
      )}
    />
  );
}
